/* ──────────────────────────────────────────────────────────────────────
 *  DimensionAdvicePanel — body for a single My Impact dimension cell.
 *
 *  Renders the dimension header (icon + name + goal medal) and, below it,
 *  the ACTIVE actionable recommendations as raised buttons. Each button
 *  opens that action's modal (wiring is a follow-up). Completed actions
 *  ('done') and future steps ('soon' / 'locked') are intentionally omitted.
 *  (Slice 1 disables every suggestion via 'soon', so the action list is
 *  currently empty and only the real medal shows — see dimensionAdvanceActions.)
 *
 *  i18n NOTE: action labels are English literals from dimensionAdvanceActions
 *  (prototype). Route through useAppTranslation + add 8-locale keys before
 *  ship, per CLAUDE.md.
 * ────────────────────────────────────────────────────────────────────── */

import React from 'react';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { DIMENSION_CONFIG } from '../DimensionRadar/dimensionConfig';
import type { DimKey } from '../DimensionRadar/dimensionConfig';
import { DIMENSION_ADVANCE } from '../../Data/Onboarding/dimensionAdvanceActions';
import type { AdvanceAction } from '../../Data/Onboarding/dimensionAdvanceActions';
import DimensionPanelHeader, { type MedalTier } from '../DimensionPanelHeader/DimensionPanelHeader';
import styles from './DimensionAdvicePanel.module.css';

interface DimensionAdvicePanelProps {
  dim: DimKey;
  /** Current tier from the Impact Signature (drives the goal medal). */
  medal?: MedalTier;
  /** Fired when an action is chosen — host opens the action modal. */
  onAction?: (dim: DimKey, action: AdvanceAction) => void;
}

export default function DimensionAdvicePanel({ dim, medal = 'none', onAction }: DimensionAdvicePanelProps) {
  const cfg = DIMENSION_CONFIG[dim];
  const adv = DIMENSION_ADVANCE[dim];

  // Active actionable recommendations only — exclude done / soon / locked.
  const actions = adv.actions.filter((a) => !a.state || a.state === 'available');

  return (
    <div
      className={styles.panel}
      style={{ '--dim-color': cfg.color } as React.CSSProperties}
    >
      {/* Real goal medal only — placeholder progress removed (Slice 1,
          HANDOFF_MYIMPACT_SUGGESTION_REMOVAL); real per-dimension progress
          arrives with DEV-1305. */}
      <DimensionPanelHeader dim={dim} medal={medal} />
      <div className={styles.actions}>
        {actions.map((a, i) => (
          <button
            key={i}
            type="button"
            className={styles.action}
            onClick={() => onAction?.(dim, a)}
          >
            <ArrowRight className={styles.lead} size={16} strokeWidth={2.2} aria-hidden="true" />
            <span className={styles.label}>{a.label}</span>
            <ChevronRight className={styles.chev} size={16} strokeWidth={2.2} aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
}
